import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { MoneyDisplay } from '@/components/shared/money-display';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MoneyInput } from '@/components/ui/money-input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { useElevateSession, isElevationCancelled } from '@/hooks/use-elevate-session';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

export interface FundAdjustBalanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fundId: string;
  fundName: string;
  currency: string;
  currentBalanceMinor: number;
}

export const FundAdjustBalanceDialog = ({
  open,
  onOpenChange,
  fundId,
  fundName,
  currency,
  currentBalanceMinor,
}: FundAdjustBalanceDialogProps) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const { elevateAndRetry, dialog: elevateDialog } = useElevateSession();

  const [newBalanceMinor, setNewBalanceMinor] = useState(currentBalanceMinor);
  const [reason, setReason] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setNewBalanceMinor(currentBalanceMinor);
      setReason('');
    }
  }

  useEffect(() => {
    if (open) setNewBalanceMinor(currentBalanceMinor);
  }, [currentBalanceMinor, open]);

  const save = useMutation({
    mutationFn: async () =>
      elevateAndRetry(
        () =>
          api.post(`/giving/funds/${fundId}/balance-adjustments`, {
            newBalanceMinor,
            reason: reason.trim(),
          }),
        { description: t('funds.detail.adjustBalance.elevateDescription') },
      ),
    onSuccess: () => {
      toast.success(t('funds.detail.adjustBalance.saved'));
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', fundId] });
      void queryClient.invalidateQueries({
        queryKey: ['giving', 'funds', fundId, 'balance-adjustments'],
      });
    },
    onError: (err) => {
      if (isElevationCancelled(err)) return;
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const reasonValid = reason.trim().length >= 3;
  const balanceChanged = newBalanceMinor !== currentBalanceMinor;
  const canSave = reasonValid && balanceChanged && !save.isPending;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="md" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('funds.detail.adjustBalance.title')}</DialogTitle>
          </DialogHeader>

          <DialogBody contentClassName="flex flex-col gap-4">
            <p className="text-body-sm text-fg-secondary">
              {t('funds.detail.adjustBalance.description', { fund: fundName })}
            </p>

            <div className="rounded-lg border border-border bg-surface-secondary px-4 py-3">
              <p className="text-caption text-fg-secondary">
                {t('funds.detail.adjustBalance.currentBalance')}
              </p>
              <MoneyDisplay
                amount={currentBalanceMinor}
                currency={currency}
                className="text-heading-sm font-semibold text-fg"
              />
            </div>

            <FormField
              label={t('funds.detail.adjustBalance.newBalance')}
              hint={t('funds.detail.adjustBalance.newBalanceHint')}
            >
              {(field) => (
                <MoneyInput
                  id={field.id}
                  value={newBalanceMinor}
                  currency={currency}
                  onChange={(value) => setNewBalanceMinor(value ?? currentBalanceMinor)}
                />
              )}
            </FormField>

            <FormField label={t('funds.detail.adjustBalance.reason')} required>
              {(field) => (
                <Textarea
                  id={field.id}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder={t('funds.detail.adjustBalance.reasonPlaceholder')}
                />
              )}
            </FormField>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={save.isPending}
              onClick={() => onOpenChange(false)}
            >
              {tCommon('action.cancel')}
            </Button>
            <Button type="button" disabled={!canSave} loading={save.isPending} onClick={() => save.mutate()}>
              {t('funds.detail.adjustBalance.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {elevateDialog}
    </>
  );
};
